"use client"

import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import FilterPanelContent from "@/app/(site)/_components/Organisms/ProductGrids/Filter/FilterPanelContent"
import { useFocusTrap } from "@/app/(site)/_utils/_hooks/useFocusTrap"
import { useHideGladlyIcon } from "@/app/(site)/_utils/_hooks/useHideGladlyIcon"
import {
    useProductGridContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { FOCUS_VISIBLE_CLASS } from "@/src/utils/a11y/focusVisible"
import { HIT_TARGET_CLASS } from "@/src/utils/a11y/hitTarget"
import { clsx } from "@/src/utils/clsx"
import { useRef } from "react"

const MOBILE_FILTER_TITLE_ID = "mobile-filter-overlay-title"

type MobileFilterOverlayProps = {
    nHits: number
}

export default function MobileFilterOverlay({ nHits }: MobileFilterOverlayProps) {
    const { filterState, isMobileFilterOpen, closeMobileFilter } = useProductGridContext()
    const { categoryTranslations } = useStaticGridContext()
    const overlayRef = useRef<HTMLDivElement>(null)

    useHideGladlyIcon(isMobileFilterOpen, { hideOnMobile: true })
    useFocusTrap(overlayRef, isMobileFilterOpen, closeMobileFilter)

    const handleClose = () => {
        closeMobileFilter()
    }

    const sortCount = filterState.sort ? 1 : 0
    const filterCount = filterState.filters.reduce((acc, f) => acc + f.data.length, 0)
    const totalCount = sortCount + filterCount

    return (
        <div
            ref={overlayRef}
            role="dialog"
            aria-modal={isMobileFilterOpen ? true : undefined}
            aria-labelledby={MOBILE_FILTER_TITLE_ID}
            aria-hidden={!isMobileFilterOpen}
            inert={!isMobileFilterOpen ? true : undefined}
            className={clsx(
                "fixed left-0 top-0 z-[var(--z-modal)] flex h-dvh w-full flex-col bg-white transition-transform duration-300 lg:hidden",
                isMobileFilterOpen ? "translate-x-0" : "-translate-x-full"
            )}
        >
            <div
                className={clsx(
                    "flex h-44 shrink-0 items-center justify-between px-12 py-8",
                    filterCount > 0 && "border-b border-col-border-neutral"
                )}
            >
                <span
                    id={MOBILE_FILTER_TITLE_ID}
                    className="pt-4 Trim_Mono_POC_Text_15_130_-5_400_uppercase"
                >
                    {categoryTranslations.filter}/{categoryTranslations.sortBy}
                    <span className="Trade_Gothic_Text_14_150_0_300">
                        {totalCount > 0 && ` (${totalCount})`}
                    </span>
                </span>
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label={categoryTranslations.hideFilterSorting}
                    className={clsx(
                        "flex h-24 w-24 items-center justify-center",
                        FOCUS_VISIBLE_CLASS,
                        HIT_TARGET_CLASS
                    )}
                >
                    <span aria-hidden="true">
                        <IconClose className="h-10 w-10" />
                    </span>
                </button>
            </div>

            <FilterPanelContent />

            <div className="shrink-0 border-t border-col-border-neutral bg-col-bg-calm p-16">
                <ButtonPill
                    onClick={handleClose}
                    color="black"
                    size="medium"
                    variant="primary"
                    fullWidth
                    className="h-40 Trim_Mono_POC_Text_14_130_0_400_uppercase"
                >
                    {categoryTranslations.showProducts.replace("{nHits}", String(nHits))}
                </ButtonPill>
            </div>
        </div>
    )
}
